
import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { useState } from 'react'
import { RequestProvider } from '../../../context/RequestContext'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { Services } from '../../../components/index/service/Services'
import { BottomButtons } from '../../../components/index/service-category/aircon-service/BottomButtons'
import Link from 'next/link'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { AirconQuickLinks } from '../../../components/index/service-category/aircon-service/AirconQuickLinks'
import { buildAirconServiceGraph } from 'utils/aircon-page-schema'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-25'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198
    const { month, year } = contentDate(CONTENT_REVIEWED);
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        wpNewPosts,
        wpPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const countryName = "Singapore"
    const requestType = "Aircon Services"

    const [ requestForm, setRequestForm ] = useState(false)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts  

    const maxRating = [1, 2, 3, 4, 5]

  return (
    <>
      <Head>
        <title>Top Reasons To Replace Aircon Parts in Singapore</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Need to replace your aircon parts? Here are the top reasons to do it sooner rather than later. StringsSG is a leading provider of aircon parts and services in Singapore. We offer a wide range of services to keep your air conditioner running smoothly and efficiently."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-parts-replacement" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Top Reasons To Replace Aircon Parts in Singapore`}/>
        <meta property="og:description" content="If your air conditioner is not working properly, it may require replacement of parts that are either damaged or malfunctioning, to help restore it's functionality." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-02T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon/2026/aircon-indoor-unit-parts-diagram.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon/2026/aircon-indoor-unit-parts-diagram.webp" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-parts-replacement"/>
        <meta name="twitter:title" content={`Top Reasons To Replace Aircon Parts in Singapore`}/> 
        <meta name="twitter:description" content="If your air conditioner is not working properly, it may require replacement of parts that are either damaged or malfunctioning, to help restore it's functionality."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-parts-replacement"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildAirconServiceGraph({
            path: '/sg/aircon-servicing-singapore/aircon-parts-replacement',
            pageName: 'Top Reasons To Replace Aircon Parts in Singapore',
            description: 'Need to replace your aircon parts? Here are the top reasons to do it sooner rather than later. StringsSG is a leading provider of aircon parts and services in Singapore. We offer a wide range of services to keep your air conditioner running smoothly and efficiently.',
            serviceName: 'Aircon Parts Replacement Singapore',
            serviceType: 'Aircon Repair',
            image: 'https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/aircon/2026/aircon-indoor-unit-parts-diagram.webp',
            datePublished: '2025-01-02T07:30:46+00:00',
            modifiedDate,
            breadcrumbName: 'Aircon Parts Replacement',
          })) }}
        />
        </Head>
        <div>
            {/* Hero H1 lives in AirconRepair; the body heading below is an H2. This
                replaced AirconServiceRequestForm, which auto-opened the question
                wizard on page load. */}
            <RequestProvider>
                <section id="aircon-parts-replacement" className='relative'>
                    <AirconRepair
                        setRequestForm={setRequestForm}
                        setIsQuickBook={setIsQuickBook}
                        title="Top Reasons To Replace Aircon Parts"
                        problem="Aircon parts replacement"
                        heroImage="aircon/2026/aircon-indoor-unit-parts-diagram.webp"
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm &&
                    <Services
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>
            <div className='leading-8'>                
                <div className='py-5 px-2 space-y-2 max-w-4xl mx-auto'>
                    <div className='py-2 space-y-5'>
                        <h2 className='text-center text-3xl md:text-4xl font-bold'>Top Reasons To Replace Aircon Parts</h2>
                        <p>
                            If your air conditioner is not working properly, it may require replacement of parts that are either damaged or malfunctioning, to help restore it&apos;s functionality.
                        </p>                        
                        <div className='relative h-96'>
                            <Image className='rounded-lg'
                                src={s3Static + "blog/living/aircon-evaporator.jpg"}
                                fill
                                sizes="(max-width: 768px) 100vw, 33vw"
                                style={{objectFit: 'cover'}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='aircon front'
                            />          
                        </div>
                        <div className='space-y-5 py-5'>
                            <div className='space-y-5'>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        1. Spoilt Aircon remote controller
                                    </h2>
                                    <p>
                                        When your aircon remote controller stops working and you tried replacing the battery to new ones, but it still does not work, then it may be spoilt.
                                        There is a possibility that the remote controller can be fixed any more. In this case buying a new remote controller is the only solution.
                                    </p>
                                    <p>
                                        Replacement Cost: $10 - $20                                     
                                    </p>
                                    <p>
                                    Buy from - <a  className='text-orange-600' href="https://www.lazada.sg/tag/universal-aircon-remote-control/" rel='noreferrer nofollow noopener'>https://www.lazada.sg/tag/universal-aircon-remote-control/</a>
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        2. Faulty Aircon Thermostat
                                    </h2>
                                    <div className='my-5 max-w-2xl'>
                                        <div className='relative h-80'>
                                            <Image className='rounded-lg'
                                                src={s3Static + "blog/living/aircon/aircon-thermostat-repair.jpg"}
                                                fill
                                                style={{objectFit: 'cover'}}
                                                placeholder='blur'
                                                blurDataURL={s3Static + "placeholder.png"}
                                                alt='Thermostat is set to 18 degrees but is not cooling'
                                            />          
                                        </div>
                                        <figcaption className='text-xs italic text-gray-500 text-center py-1'>Thermostat is set to 18 degrees but is not cooling</figcaption>
                                    </div> 
                                    <p>
                                        A faulty aircon thermostat can cause various issues with your air conditioning system, leading to discomfort, inefficiency, or even potential damage if left 
                                        unaddressed.
                                    </p>
                                    <p>
                                        Replacement Cost: $100 - $350                              
                                    </p>
                                    <p>
                                        Get a professional for aircon thermostat replacement - 
                                        <Link href="/sg/aircon-servicing-singapore/aircon-thermostat" className='text-orange-600'> https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-thermostat</Link>
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        3. Aircon Capacitor
                                    </h2>
                                    <div className='my-5 max-w-2xl'>
                                        <div className='relative h-80'>
                                            <Image className='rounded-lg'
                                                src={s3Static + "blog/living/aircon/aircon-capacitor.jpg"}
                                                fill
                                                style={{objectFit: 'cover'}}
                                                placeholder='blur'
                                                blurDataURL={s3Static + "placeholder.png"}
                                                alt='Aircon capacitor is being replaced'
                                            />          
                                        </div>
                                        <figcaption className='text-xs italic text-gray-500 text-center py-1'>Aircon capacitor is being replaced</figcaption>
                                    </div> 
                                    <p>
                                        The Capacitor is a small device responsible for distribution of electricity in the aircon unit. A spoilt capacitor will not be able to 
                                        supply electricity to the vital components of the Aircon, which will result in your air con not working at all. The capacitor gets damaged
                                        if your aircon is constantly over worked, meaning it often keeps running for long hours.
                                    </p>
                                    <p>
                                        Replacement Cost: $14 - $20                                     
                                    </p>
                                    <p>
                                        Buy from - <a  className='text-orange-600' href="https://www.amazon.com/ac-capacitors/s?k=ac+capacitors" rel='noreferrer nofollow noopener'>https://www.amazon.com/ac-capacitors/s?k=ac+capacitors</a>
                                    </p>
                                    <p>
                                        Get a professional for aircon capacitor replacement - 
                                        <Link href="/sg/aircon-servicing-singapore/aircon-capacitor" className='text-orange-600'> https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-capacitor</Link>
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        4. Evaporator coil or Condenser coil is damaged
                                    </h2>
                                    <div className='my-5'>
                                        <div className='relative h-96 w-full'>
                                            <Image className='rounded-lg'
                                                src={s3Static + "blog/living/dirty-aircon-evaporator-coil.jpg"}
                                                fill
                                                style={{objectFit: 'cover', objectPosition: 'top right'}}
                                                placeholder='blur'
                                                blurDataURL={s3Static + "placeholder.png"}
                                                alt='dirty aircon evaporator coil'
                                            />          
                                        </div>
                                        <figcaption className='text-xs italic text-gray-500 text-center py-1'>Dirty evaporator coil like this would need chemical wash</figcaption>
                                    </div>
                                    <p>
                                        The evaporator and condenser coil can collect grease dirt because of a mixture of dirt in the air and the water from condensation, which
                                        results in ineffective cooling. Gradually over time if the coils are left uncleaned this moist environment may lead to corrosion and eventually
                                        holes on the coil. Corrosion of the evaporator coil or condenser coil can result in complete failure of the aircon functionality.
                                    </p>
                                    <p>
                                        Replacement Cost: $800 - $2000 depending on the capacity and brand                                 
                                    </p>
                                    <p>
                                        Contact your Aircon Service Center as it may be covered by warranty.
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        5. Damaged drain pan
                                    </h2>
                                    <p>
                                        The Drainage pan is responsible for collecting water from condensation and transferring it to the drainage pipes. A corroded or broken 
                                        drainage pan will result in water leakage. Sometimes this can be fixed with super glue, but if the damage is severe we recommend replacing
                                        the drainage pan.
                                    </p>
                                    <p>
                                        Replacement Cost: $40 - $80                                
                                    </p>
                                    <p>
                                        Contact your Aircon Service Center as it may be covered by warranty.
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        6. Malfunctioning or spoilt indoor unit fan motor
                                    </h2>
                                    <p>
                                        The fan motor is a device which is responsible for spinning the fan blower. Excessive long running hours of your aircon cn over heat the 
                                        motor and spoil it, resulting in complete failure of air conditioning. If such a situation arise, a complete replacement of the 
                                        motor is essential.
                                    </p>
                                    <p>
                                        Replacement Cost: $150 - $450                                
                                    </p>
                                    <p>
                                    Buy from - <a  className='text-orange-600' href="https://www.amazon.com/air-conditioner-fan-motor/s?k=air+conditioner+fan+motor" rel='noreferrer nofollow noopener'>https://www.amazon.com/air-conditioner-fan-motor/s?k=air+conditioner+fan+motor</a>
                                    </p>
                                    <p>
                                        Get a professional for indoor fan motor replacement - 
                                        <Link href="/sg/aircon-servicing-singapore/aircon-fan-motor" className='text-orange-600'> https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-fan-motor</Link>
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        7. Faulty Condenser Fan Motor
                                    </h2>
                                    <p>
                                        The condenser fan motor is responsible for rotating the condenser fan which helps to release hot air from indoor to the outdoor environment.
                                        A faulty condenser fan motor in an air conditioning system can lead to various issues like improper cooling, loud noise and sometimes complete shutdown of the 
                                        A/C system
                                    </p>
                                    <p>
                                        Replacement Cost: $250 - $750                                
                                    </p>
                                    <p>
                                        Get a professional for condenser fan motor replacement - 
                                        <Link href="/sg/aircon-servicing-singapore/condenser-fan-motor" className='text-orange-600'> https://www.stringssg.com/sg/aircon-servicing-singapore/condenser-fan-motor</Link>
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        8. Damaged Aircon Blower
                                    </h2>
                                    <div className='my-5'>
                                        <div className='relative h-96 w-full'>
                                            <Image className='rounded-lg'
                                                src={s3Static + "blog/living/aircon/technicain-checking-air-conditioner.jpg"}
                                                fill
                                                style={{objectFit: 'cover', objectPosition: 'top'}}
                                                placeholder='blur'
                                                blurDataURL={s3Static + "placeholder.png"}
                                                alt='Technician checking the aircon blower'
                                            />          
                                        </div>
                                        <figcaption className='text-xs italic text-gray-500 text-center py-1'>Technician checking the aircon blower</figcaption>
                                    </div> 
                                    <p>
                                        Damaged aircon blower will result in improper airflow and hence reduced cooling. It is one of the most vital component of the air conditioning
                                        system and must be replaced immediately if damaged.
                                    </p>
                                    <p>
                                        Replacement Cost: $200 to $800                             
                                    </p>
                                    <p>
                                        Get an aircon technician to fix the blower - 
                                        <Link href="/sg/aircon-servicing-singapore/aircon-blower" className='text-orange-600'> https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-blower</Link>
                                    </p>
                                </div>
                                <div className='space-y-2'>
                                    <h2 className='text-lg font-bold'>
                                        9. Malfunctioning Aircon PCB
                                    </h2>
                                    <div className='my-5 max-w-3xl'>
                                        <div className='relative h-96 w-full'>
                                            <Image className='rounded-lg'
                                                src={s3Static + "blog/living/aircon-printed-circuit-board.jpg"}
                                                fill
                                                style={{objectFit: 'cover'}}
                                                placeholder='blur'
                                                blurDataURL={s3Static + "placeholder.png"}
                                                alt='Air conditioner PCB'
                                            />          
                                        </div>
                                        <figcaption className='text-xs italic text-gray-500 text-center py-1'>Air conditioner PCB</figcaption>
                                    </div> 
                                    <p>
                                        If there is a problem with the aircon PCB, it can affect the overall performance of the air conditioning system. In such cases, a trained 
                                        technician may need to diagnose the issue and repair or replace the faulty PCB to restore proper operation of the air conditioning unit.
                                    </p>
                                    <p>
                                        Indoor PCB Replacement Cost: $200 - $380                            
                                    </p>
                                    <p>
                                        Outdoor PCB Replacement Cost: $350 - $800                           
                                    </p>
                                    <p>
                                        Get professional help Aircon PCB replacement - 
                                        <Link href="/sg/aircon-servicing-singapore/ac-printed-circuit-board-pcb" className='text-orange-600'> https://www.stringssg.com/sg/aircon-servicing-singapore/ac-printed-circuit-board-pcb</Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>  
                </div> 
                <div className='text-center py-10 relative'>
                    <div className='relative w-full h-120'>
                        <Image
                            src={s3Static + "blog/living/repairman-doing-air-conditioner-service.jpg"}
                            fill
                            style={{objectFit: 'cover', objectPosition: 'center'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='Join us as an Aircon Service Provider - StringsSG'
                        />     
                    </div> 
                    <div className='absolute flex items-center text-white top-0 bottom-0 left-0 right-0 w-fit mx-auto rounded-3xl'>
                        <Link href="/sg/aircon-servicing-singapore" prefetch={false} className='p-4 rounded-lg bg-black/60 text-xl md:text-2xl'>
                                Request for Aircon Parts Replacement
                        </Link>       
                    </div>  
                </div>
                <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
                {/* <AirconQuickLinks/> */}
            </div>
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
